// @flow
import type { Setting, SettingValue, SettingsLogic } from '~/core/constants/actions';
import type { ActionFormSettings, FormStatus } from '~/core/state/actions/types';
import type { ExtendedFormAttribute } from '../types';

import React, { Component } from 'react';
import { noop } from 'lodash/fp';
import { features } from '~/core/constants/subscriptions';
import { deviceTypes } from '~/core/constants/devices/';
import { FormattedMessage } from 'react-intl';
import { actionTypes } from '~/core/constants/actions';
import generalIcons from '~/core/constants/icons/general-svg';
import { BounceIn, BounceOut } from '~/core/utils/velocity-animations';
import commonMessages from '~/core/constants/actions/intl';
import { Checkbox, Schedule, Select } from './attribute-types';
import messages from './intl';

import {
    ActionContainer,
    AttributeIcon,
    AttributeIconContainer,
    Description,
    Detail,
    DetailItem,
    Header,
    Main,
    MoreIcon,
    RemoveAttribute,
    TextSection,
    TickIcon,
    Title
} from './styles';

const defineSettingsList = (
    logic: SettingsLogic,
    settingsDefinitions: { [string]: Setting },
    currentSettingsValues: ?ActionFormSettings,
    previousSettings = []
) =>
    logic.reduce((acc, l) => {
        const parentSetting = settingsDefinitions[l.settingId];
        const currentSettings = [...acc, parentSetting];

        const currentParentValue =
            currentSettingsValues && currentSettingsValues[parentSetting.propertyName]
                ? currentSettingsValues[parentSetting.propertyName]
                : parentSetting.defaultValue;
        const childLogic = l.ifValue && l.ifValue[currentParentValue];
        return childLogic
            ? defineSettingsList(childLogic, settingsDefinitions, currentSettingsValues, currentSettings)
            : currentSettings;
    }, previousSettings);

const prepareSettings = (
    logic: SettingsLogic,
    settingsDefinitions: { [string]: Setting },
    currentSettingsValues: ActionFormSettings,
    changedSettingId: string,
    newValue: any,
    previousSettings = {}
) =>
    logic.reduce((acc, l) => {
        const parentSetting = settingsDefinitions[l.settingId];
        const currentParentValue =
            changedSettingId === l.settingId
                ? newValue
                : currentSettingsValues[parentSetting.propertyName] != null
                  ? currentSettingsValues[parentSetting.propertyName]
                  : parentSetting.defaultValue;

        const currentSettings = { ...acc, [parentSetting.propertyName]: currentParentValue };

        const childLogic = l.ifValue && l.ifValue[currentParentValue];
        return childLogic
            ? prepareSettings(
                  childLogic,
                  settingsDefinitions,
                  currentSettingsValues,
                  changedSettingId,
                  newValue,
                  currentSettings
              )
            : currentSettings;
    }, previousSettings);

const getDurationList = (status: ?string): Array<SettingValue> => [
    { value: 0, label: status === 'ON' ? commonMessages.durationStayOn : commonMessages.durationStayOff },
    { value: 30, label: commonMessages.duration30Seconds },
    { value: 60, label: commonMessages.duration1Minute },
    { value: 180, label: commonMessages.duration3Minutes },
    { value: 300, label: commonMessages.duration5Minutes },
    { value: 600, label: commonMessages.duration10Minutes },
    { value: 900, label: commonMessages.duration15Minutes },
    { value: 1800, label: commonMessages.duration30Minutes },
    { value: 3600, label: commonMessages.duration1Hour },
    { value: 7200, label: commonMessages.duration2Hours },
    { value: 10800, label: commonMessages.duration3Hours },
    { value: 14400, label: commonMessages.duration4Hours },
    { value: 18000, label: commonMessages.duration5Hours },
    { value: 21600, label: commonMessages.duration6Hours }
];

type Props = {
    actionId: ?string,
    event: ExtendedFormAttribute,
    hasSMS: boolean,
    formStatus: FormStatus,
    removeActionAttribute: ExtendedFormAttribute => {},
    setActionAttribute: (string, any) => {},
    showHiveLiveModal: noop,
    identifyLight: noop
};
type State = {
    isDetailVisible: boolean,
    wasDetailSeen: boolean
};
export default class ActionAttribute extends Component<Props, State> {
    state = {
        isDetailVisible: false,
        wasDetailSeen: false
    };

    showDetail = () => this.setState({ isDetailVisible: true, wasDetailSeen: true });

    hideDetail = () => this.setState({ isDetailVisible: false });

    attributeTitle = (chooseLabel: $npm$ReactIntl$MessageDescriptor) => {
        const { event: { definition, devices, id }, formStatus } = this.props;
        const { wasDetailSeen } = this.state;

        const selectedDevice = devices.find(d => d.value === id);
        const heatingHotwater = (definition.devices || []).some(device =>
            [deviceTypes.HEATING, deviceTypes.HOT_WATER].includes(device)
        );

        return selectedDevice &&
            !heatingHotwater &&
            (formStatus === 'edit' || ((formStatus === 'new' && wasDetailSeen) || devices.length === 1)) ? (
            selectedDevice.label
        ) : (
            <FormattedMessage {...chooseLabel} />
        );
    };

    removeAttribute = () => {
        const { event, removeActionAttribute } = this.props;
        removeActionAttribute(event);
    };

    handleChange = (settings: ActionFormSettings, settingId: string, newValue: any) => {
        const { event: { definition }, hasSMS, setActionAttribute, showHiveLiveModal } = this.props;

        settingId === 'text' && !hasSMS
            ? showHiveLiveModal({ feature: features.textNotifications })
            : setActionAttribute(
                  'settings',
                  definition.settings
                      ? prepareSettings(
                            definition.settings.logic,
                            definition.settings.definitions,
                            settings,
                            settingId,
                            newValue
                        )
                      : {}
              );
    };

    render() {
        const { actionId, event, setActionAttribute } = this.props;
        const { isDetailVisible } = this.state;

        const { definition, devices, duration, id, settings: preSettings } = event;
        const settings =
            preSettings && definition.processSettings ? definition.processSettings(preSettings) : preSettings;
        const settingsList = definition.settings
            ? defineSettingsList(definition.settings.logic, definition.settings.definitions, settings)
            : [];
        const visibleSettings = settingsList.filter(setting => !setting.hide);
        const detailsLength = (devices.length > 1 ? 1 : 0) + visibleSettings.length + (definition.hasDuration ? 1 : 0);

        const chooseLabel = definition.chooseLabel || messages.defaultChooseLabel;

        const removable = event.group !== 'when';

        return (
            <Main
                detailsLength={detailsLength}
                isDetailVisible={isDetailVisible}
                isSchedule={definition.componentType === 'schedule'}
                removable={removable}
            >
                <Header
                    clickable={detailsLength && !isDetailVisible}
                    onClick={detailsLength && !isDetailVisible ? this.showDetail : null}
                >
                    <AttributeIconContainer>
                        <AttributeIcon quickAction={event.type === actionTypes.QUICK_ACTION} type={definition.icon} />
                    </AttributeIconContainer>
                    <TextSection>
                        <Title>{devices.length === 1 ? devices[0].label : this.attributeTitle(chooseLabel)}</Title>
                        {definition.description && (
                            <Description>
                                <FormattedMessage {...definition.description} />
                            </Description>
                        )}
                    </TextSection>
                    {detailsLength ? (
                        <ActionContainer
                            enter={{ animation: BounceIn, duration: 300 }}
                            leave={{ animation: BounceOut, duration: 300 }}
                            onClick={isDetailVisible ? this.hideDetail : null}
                        >
                            {isDetailVisible ? (
                                <TickIcon key="tick" type={generalIcons.TICK_SIMPLE} />
                            ) : (
                                <MoreIcon key="more" type={generalIcons.MORE} />
                            )}
                        </ActionContainer>
                    ) : null}
                </Header>
                <Detail>
                    {devices.length > 1 && (
                        <DetailItem>
                            <Select
                                currentValue={id}
                                handleChange={deviceId => setActionAttribute('id', deviceId)}
                                label={chooseLabel}
                                options={devices}
                            />
                        </DetailItem>
                    )}
                    {visibleSettings.map(
                        s =>
                            definition.componentType === 'schedule' ? (
                                <DetailItem isSchedule key={s.id}>
                                    <Schedule actionId={actionId} />
                                </DetailItem>
                            ) : (
                                <DetailItem key={s.id}>
                                    {definition.componentType === 'checkbox' ? (
                                        <Checkbox
                                            currentValue={
                                                settings && settings[s.propertyName] != null
                                                    ? settings[s.propertyName]
                                                    : s.defaultValue
                                            }
                                            handleChange={newValue => this.handleChange(settings || {}, s.id, newValue)}
                                            label={s.chooseLabel}
                                        />
                                    ) : (
                                        <Select
                                            currentValue={
                                                settings && settings[s.propertyName]
                                                    ? settings[s.propertyName]
                                                    : s.defaultValue
                                            }
                                            handleChange={newValue =>
                                                setActionAttribute(
                                                    'settings',
                                                    definition.settings
                                                        ? prepareSettings(
                                                              definition.settings.logic,
                                                              definition.settings.definitions,
                                                              settings || {},
                                                              s.id,
                                                              newValue
                                                          )
                                                        : {}
                                                )}
                                            label={s.chooseLabel}
                                            options={s.values}
                                        />
                                    )}
                                </DetailItem>
                            )
                    )}
                    {definition.hasDuration && (
                        <DetailItem>
                            <Select
                                currentValue={duration || 0}
                                handleChange={newDuration => setActionAttribute('duration', newDuration)}
                                label={commonMessages.forDuration}
                                options={getDurationList(
                                    settings ? settings.status : (visibleSettings[0] || {}).defaultValue
                                )}
                            />
                        </DetailItem>
                    )}
                    {removable && (
                        <RemoveAttribute onClick={this.removeAttribute}>
                            <FormattedMessage {...messages.removeFromAction} />
                        </RemoveAttribute>
                    )}
                </Detail>
            </Main>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/actions/action-attribute/component.js